import React, { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useApolloClient } from '@apollo/react-hooks'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from '@/components/ui-compat'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { faFolder, faSave } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import { toastr } from 'react-redux-toastr'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'

interface FolderModalProps {
  isOpen: boolean
  onClose: (saved?: boolean) => void
  folderId: string | null
  parentFolderId: string | null
  projectId?: string | null
}

const FolderModal: React.FC<FolderModalProps> = ({
  isOpen,
  onClose,
  folderId,
  parentFolderId,
  projectId,
}) => {
  const { t } = useTranslation()
  const isEditing = !!folderId
  const client = useApolloClient()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  // Snapshot of the project the user was looking at when the modal was opened.
  // If `me.currentProject` changes while the modal is open (e.g. user switches
  // project from sidebar, or another tab does it), the snapshot lets us detect
  // the mismatch and abort instead of silently creating the folder in the wrong
  // project. Reset whenever the modal opens.
  const openedProjectIdRef = useRef<string | null | undefined>(null)
  useEffect(() => {
    if (isOpen) openedProjectIdRef.current = projectId
  }, [isOpen, projectId])

  // Auto-close if the current project changes while the modal is open — surface
  // the change to the user instead of letting them submit against a stale state.
  useEffect(() => {
    if (!isOpen || isEditing) return
    if (openedProjectIdRef.current && projectId && openedProjectIdRef.current !== projectId) {
      toastr.warning(
        t('Common_attention') || '',
        t('DocControl_folder_modal_project_changed') ||
          'Project changed, closing dialog.'
      )
      onClose(false)
    }
  }, [projectId, isOpen, isEditing, onClose, t])

  // Fetch folder data if editing
  const { data: folderData, loading: loadingFolder } = useQuery(Query.DOC_FOLDER, {
    variables: { id: folderId },
    skip: !folderId,
    fetchPolicy: 'network-only',
  })

  // Create mutation
  const [createFolder, { loading: creating }] = useMutation(Mutation.CREATE_DOC_FOLDER, {
    refetchQueries: projectId ? [{ query: Query.DOC_FOLDER_TREE, variables: { projectId } }] : [],
    onCompleted: () => {
      toastr.success('', t('DocControl_folder_created'))
      onClose(true)
    },
    onError: (error) => {
      toastr.error(t('Common_error'), error.message)
    },
  })

  // Update mutation
  const [updateFolder, { loading: updating }] = useMutation(Mutation.UPDATE_DOC_FOLDER, {
    refetchQueries: projectId ? [{ query: Query.DOC_FOLDER_TREE, variables: { projectId } }] : [],
    onCompleted: () => {
      toastr.success('', t('DocControl_folder_updated'))
      onClose(true)
    },
    onError: (error) => {
      toastr.error(t('Common_error'), error.message)
    },
  })

  // Populate form when editing
  useEffect(() => {
    if (folderData?.docFolder) {
      setName(folderData.docFolder.name || '')
      setDescription(folderData.docFolder.description || '')
    }
  }, [folderData])

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen && !folderId) {
      setName('')
      setDescription('')
    }
  }, [isOpen, folderId])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      toastr.warning('', t('DocControl_folder_name_required'))
      return
    }

    if (isEditing) {
      await updateFolder({
        variables: {
          id: folderId,
          input: {
            name: name.trim(),
            description: description.trim() || null,
          },
        },
      })
    } else {
      // For root folders, re-read currentProject from the live Apollo cache at
      // submit time. The `projectId` prop is captured at modal-open and can go
      // stale if the user switches projects, which previously caused folders to
      // land in the wrong project. The cache value is always fresh.
      let resolvedProjectId: string | null = null
      if (!parentFolderId) {
        try {
          const meCache: any = client.readQuery({ query: Query.ME })
          resolvedProjectId = meCache?.me?.currentProject?.id || projectId || null
        } catch {
          resolvedProjectId = projectId || null
        }
        if (!resolvedProjectId) {
          toastr.error(t('Common_error'), t('DocControl_folder_no_project'))
          return
        }
        if (openedProjectIdRef.current && resolvedProjectId !== openedProjectIdRef.current) {
          toastr.warning(
            t('Common_attention') || '',
            t('DocControl_folder_modal_project_changed') ||
              'Project changed, closing dialog.'
          )
          onClose(false)
          return
        }
      }

      await createFolder({
        variables: {
          input: {
            name: name.trim(),
            description: description.trim() || null,
            parentId: parentFolderId,
            // projectId is required for root folders (no parentId)
            projectId: parentFolderId ? null : resolvedProjectId,
          },
        },
      })
    }
  }

  const loading = creating || updating || loadingFolder

  return (
    <Modal isOpen={isOpen} toggle={() => onClose()} centered>
      <Form onSubmit={handleSubmit}>
        <ModalHeader toggle={() => onClose()}>
          <Icon icon={faFolder as any} className="mr-2 text-[hsl(var(--warning))]" />
          {isEditing ? t('DocControl_edit_folder') : t('DocControl_create_folder')}
        </ModalHeader>

        <ModalBody>
          {loadingFolder ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : (
            <>
              <FormGroup>
                <Label for="folderName">{t('DocControl_folder_name')} *</Label>
                <Input
                  type="text"
                  id="folderName"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('DocControl_folder_name_placeholder')}
                  required
                  autoFocus
                />
              </FormGroup>

              <FormGroup>
                <Label for="folderDescription">{t('DocControl_folder_description')}</Label>
                <Input
                  type="textarea"
                  id="folderDescription"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('DocControl_folder_description_placeholder')}
                  rows={3}
                />
              </FormGroup>

              {!isEditing && parentFolderId && (
                <small className="text-muted-foreground">
                  {t('DocControl_folder_will_be_created_in_parent')}
                </small>
              )}
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={() => onClose()} disabled={loading}>
            {t('Common_cancel')}
          </Button>
          <Button color="primary" type="submit" disabled={loading}>
            {loading ? (
              <Spinner size="sm" />
            ) : (
              <>
                <Icon icon={faSave as any} className="mr-1" />
                {isEditing ? t('Common_save') : t('Common_create')}
              </>
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default FolderModal
